import type {
  ChessAction,
  ChessState,
  Color,
  MoveAction,
  MoveType,
  PromotablePieceType,
  ResignAction,
  SelectAugmentationAction,
  Square,
} from "@cloudnest/augmented-chess-engine";
import { normalizeChessState } from "@cloudnest/augmented-chess-engine";

type JsonObject = Record<string, unknown>;

const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;
const RANKS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
const PROMOTABLE_PIECES = ["QUEEN", "ROOK", "BISHOP", "KNIGHT"] as const;
const MOVE_TYPES = [
  "NORMAL",
  "CAPTURE",
  "CASTLE_KINGSIDE",
  "CASTLE_QUEENSIDE",
  "EN_PASSANT",
  "PROMOTION",
] as const;

export type ChessReplayEvent =
  | {
      readonly eventType: "init";
      readonly payload: { readonly state: ChessState };
    }
  | {
      readonly eventType: "action";
      readonly payload: ChessAction;
    }
  | {
      readonly eventType: "select_augmentation";
      readonly payload: SelectAugmentationAction;
    }
  | {
      readonly eventType: "move";
      readonly payload: MoveAction;
    }
  | {
      readonly eventType: "resign";
      readonly payload: ResignAction;
    }
  | {
      readonly eventType: "game_over";
      readonly payload: JsonObject;
    };

export interface ChessEventInput {
  readonly eventType: string;
  readonly payload?: Record<string, unknown>;
}

function isObject(value: unknown): value is JsonObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isFile(value: unknown): value is Square["file"] {
  return typeof value === "string" && FILES.includes(value as Square["file"]);
}

function isRank(value: unknown): value is Square["rank"] {
  return typeof value === "number" && RANKS.includes(value as Square["rank"]);
}

function squareFrom(value: unknown, field: string): Square {
  if (!isObject(value) || !isFile(value.file) || !isRank(value.rank)) {
    throw new Error(`Chess replay event has invalid ${field}`);
  }
  return { file: value.file, rank: value.rank };
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function optionalSquare(value: unknown, field: string): Square | undefined {
  if (value === undefined || value === null) return undefined;
  return squareFrom(value, field);
}

function optionalPromotable(value: unknown): PromotablePieceType | undefined {
  return typeof value === "string" &&
    PROMOTABLE_PIECES.includes(value as PromotablePieceType)
    ? (value as PromotablePieceType)
    : undefined;
}

function optionalMoveType(value: unknown): MoveType | undefined {
  return typeof value === "string" && MOVE_TYPES.includes(value as MoveType)
    ? (value as MoveType)
    : undefined;
}

function timestampFrom(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function colorFrom(value: unknown): Color {
  if (value === "WHITE" || value === "BLACK") return value;
  throw new Error("Chess replay event has invalid player color");
}

function actionPayload(event: ChessEventInput): JsonObject {
  const payload = event.payload ?? {};
  if (!isObject(payload)) {
    throw new Error(
      `Chess replay event ${event.eventType} has invalid payload`,
    );
  }
  return isObject(payload.action) ? payload.action : payload;
}

function moveActionFromPayload(payload: JsonObject): MoveAction {
  if (typeof payload.pieceId !== "string") {
    throw new Error("Chess move replay event is missing pieceId");
  }
  return {
    type: "MOVE",
    pieceId: payload.pieceId,
    from: squareFrom(payload.from, "from"),
    to: squareFrom(payload.to, "to"),
    capturedPieceId: optionalString(payload.capturedPieceId),
    promoteTo: optionalPromotable(payload.promoteTo),
    moveType: optionalMoveType(payload.moveType),
    enPassantCaptureSquare: optionalSquare(
      payload.enPassantCaptureSquare,
      "enPassantCaptureSquare",
    ),
    capturedEnRoutePieceId: optionalString(payload.capturedEnRoutePieceId),
    capturedEnRouteSquare: optionalSquare(
      payload.capturedEnRouteSquare,
      "capturedEnRouteSquare",
    ),
    isMutualDestruction: optionalBoolean(payload.isMutualDestruction),
    timestamp: timestampFrom(payload.timestamp),
  };
}

function selectActionFromPayload(
  payload: JsonObject,
): SelectAugmentationAction {
  if (typeof payload.augmentationId !== "string") {
    throw new Error("Chess selection replay event is missing augmentationId");
  }
  return {
    type: "SELECT_AUGMENTATION",
    player: colorFrom(payload.player),
    augmentationId: payload.augmentationId,
    timestamp: timestampFrom(payload.timestamp),
  };
}

function resignActionFromPayload(payload: JsonObject): ResignAction {
  return {
    type: "RESIGN",
    color: colorFrom(payload.color),
    timestamp: timestampFrom(payload.timestamp),
  };
}

function chessActionFromPayload(payload: JsonObject): ChessAction {
  if (typeof payload.type !== "string") {
    throw new Error("Chess action replay event is missing action type");
  }

  switch (payload.type) {
    case "MOVE":
      return moveActionFromPayload(payload);
    case "SELECT_AUGMENTATION":
      return selectActionFromPayload(payload);
    case "RESIGN":
      return resignActionFromPayload(payload);
    default:
      throw new Error(`Unsupported Chess action type: ${payload.type}`);
  }
}

export function toChessReplayEvent(event: ChessEventInput): ChessReplayEvent {
  const payload = event.payload ?? {};
  if (!isObject(payload)) {
    throw new Error(
      `Chess replay event ${event.eventType} has invalid payload`,
    );
  }

  switch (event.eventType) {
    case "init": {
      if (!isObject(payload.state)) {
        throw new Error("Chess init replay event is missing state");
      }
      return {
        eventType: "init",
        payload: { state: normalizeChessState(payload.state) },
      };
    }
    case "action":
      return {
        eventType: "action",
        payload: chessActionFromPayload(actionPayload(event)),
      };
    case "select_augmentation":
      return {
        eventType: "select_augmentation",
        payload: selectActionFromPayload(payload),
      };
    case "move":
      return {
        eventType: "move",
        payload: moveActionFromPayload(payload),
      };
    case "resign":
      return {
        eventType: "resign",
        payload: resignActionFromPayload(payload),
      };
    case "game_over":
      return { eventType: "game_over", payload };
    default:
      throw new Error(
        `Unsupported Chess replay event type: ${event.eventType}`,
      );
  }
}
